import { useMemo } from 'react';
import { EllipsesMenu } from 'src/components';
import { useProjectActions } from 'src/components/SidePanelTabs/Projects/use-project-actions';
import { useSiteActions } from 'src/components/SiteComponents/use-site-actions';
import { Address, Project, useAddress, useDeleteProject } from 'src/graphql';
import { useConfirmationModal } from 'src/utils/hooks';

interface SiteMenuProps {
  address: Address;
  project: Project;
  onDelete?: () => void;
}

export const ProjectMenu = ({ address, project, onDelete }: SiteMenuProps) => {
  const { confirm } = useConfirmationModal();
  const { deleteProject } = useDeleteProject();
  const { updateAddress } = useAddress(address.addressId);

  const { updateSite, copySiteId, copySiteUrl, changeSites, disassociateSite } =
    useSiteActions({
      address,
    });

  const { copyProjectId, copyProjectUrl } = useProjectActions({ address, project });

  const menuOptions = useMemo(
    () =>
      [
        updateSite,
        changeSites,
        disassociateSite,
        copySiteId,
        copySiteUrl,
        copyProjectId,
        copyProjectUrl,
        {
          label: 'Delete',
          icon: 'delete',
          isAllowed: !!project?.id,
          handler: async () => {
            const confirmed = await confirm({
              body: 'Please confirm you want \nto delete project from the site.',
              variant: 'delete',
            });
            if (confirmed) {
              if (address.primaryProjectId === project?.id) {
                await updateAddress({
                  ...address,
                  primaryProjectId: null,
                });
              }
              const { errors } = await deleteProject(project?.id);
              if (errors) {
                console.error(errors);
                return;
              }
              if (onDelete) {
                onDelete();
              }
            }
          },
        },
      ]
        .filter((option) => ('isAllowed' in option ? option.isAllowed : true))
        .map((option) => ({
          ...option,
          onClick: option.handler,
        })),
    [
      updateSite,
      changeSites,
      disassociateSite,
      copySiteId,
      copySiteUrl,
      copyProjectId,
      copyProjectUrl,
      project?.id,
      confirm,
      address,
      deleteProject,
      onDelete,
      updateAddress,
    ],
  );

  if (!menuOptions.length) {
    return null;
  }

  return <EllipsesMenu options={menuOptions} />;
};
